import type { Opaque } from "ts-essentials";
import { z } from "zod";

/**
 * Neutral base vocabulary for dynamic-content block skeletons: an ordered
 * list of text + condition placements whose branch fragments live on
 * `content_dynamic_slot_contents`. LCM (`lib/llm/lifecycle-studio/dynamic-content`)
 * and CEP (`lib/cep/types/composer-blocks.ts`) both extend this module —
 * neither product imports the other's types.
 */

export type SlotUsageId = Opaque<string, "SlotUsageId">;

/** Every block id (and usage id) is a `crypto.randomUUID()` from its writer. */
export const blockIdSchema = z.string().uuid();

export const baseTextBlockSchema = z.object({
  type: z.literal("text"),
  id: blockIdSchema,
  text: z.string(),
});

export const baseConditionBlockSchema = z.object({
  type: z.literal("condition"),
  id: blockIdSchema,
  usageId: blockIdSchema,
});

export type BaseTextBlock = { type: "text"; id: string; text: string };
export type BaseConditionBlock = {
  type: "condition";
  id: string;
  usageId: SlotUsageId;
};
export type BaseContentBlock = BaseTextBlock | BaseConditionBlock;

const baseContentBlockSchema = z.discriminatedUnion("type", [
  baseTextBlockSchema,
  baseConditionBlockSchema,
]);

const BASE_BLOCK_TYPES = new Set<string>(["text", "condition"]);

/** Names the offending `type` for extension blocks (e.g. CEP's `link`) so
 * strict consumers can report them without ever naming another product's
 * vocabulary. */
function describeRejectedBlock(item: unknown): string {
  if (item !== null && typeof item === "object" && !Array.isArray(item)) {
    const type = (item as { type?: unknown }).type;
    if (typeof type === "string" && !BASE_BLOCK_TYPES.has(type)) {
      return `unsupported block type "${type}"`;
    }
  }
  return "malformed block";
}

export type ParseBaseContentBlocksResult =
  | { ok: true; blocks: BaseContentBlock[] }
  | { ok: false; error: string };

/** Blocks persisted before writers minted ids lack an `id` — the parse mints
 * one so parsed blocks always carry it. Uses the `crypto` global, not
 * node:crypto, so this module stays bundleable in the app. */
function withMintedBlockId(item: unknown): unknown {
  if (
    item !== null &&
    typeof item === "object" &&
    !Array.isArray(item) &&
    (item as { id?: unknown }).id === undefined
  ) {
    return { ...item, id: crypto.randomUUID() };
  }
  return item;
}

export function parseBaseContentBlocksResult(
  value: unknown,
): ParseBaseContentBlocksResult {
  if (!Array.isArray(value)) {
    return { ok: false, error: "blocks must be an array" };
  }
  const blocks: BaseContentBlock[] = [];
  for (const item of value) {
    const parsed = baseContentBlockSchema.safeParse(withMintedBlockId(item));
    if (!parsed.success) {
      return { ok: false, error: describeRejectedBlock(item) };
    }
    blocks.push(parsed.data as BaseContentBlock);
  }
  return { ok: true, blocks };
}

export function parseBaseContentBlocks(
  value: unknown,
): BaseContentBlock[] | null {
  const result = parseBaseContentBlocksResult(value);
  return result.ok ? result.blocks : null;
}
